/**
 * Settings Utility
 * Persists and retrieves user settings from localStorage
 */
import { buildSettingsBaselineDefaults } from './sportConfig';

const SETTINGS_KEY = 'foresight-settings';

// All individual events ordered by distance (small to large)
// This is the master list of events with their default configurations per level
// Levels: middle (middle school), high (high school), college (college/professional)
// null values indicate the event is not typically run at that level
// All durations are in 30-second intervals (e.g., '02:00', '02:30', '03:00')
export const ALL_EVENTS = [
  // Sprints (shortest first)
  { 
    id: '55m', name: '55m Dash', type: 'sprint',
    levels: {
      middle: { heats: 6, duration: '01:30' },
      high: { heats: 12, duration: '02:00' },
      college: { heats: 4, duration: '02:30' }
    }
  },
  { 
    id: '60m', name: '60m Dash', type: 'sprint',
    levels: {
      middle: { heats: 6, duration: '01:30' },
      high: { heats: 15, duration: '02:00' },
      college: { heats: 4, duration: '02:30' }
    }
  },
  { 
    id: '100m', name: '100m Dash', type: 'sprint',
    levels: {
      middle: { heats: 5, duration: '02:00' },
      high: { heats: 10, duration: '02:00' },
      college: { heats: 3, duration: '02:00' }
    }
  },
  { 
    id: '200m', name: '200m Dash', type: 'sprint',
    levels: {
      middle: { heats: 5, duration: '02:00' },
      high: { heats: 10, duration: '02:00' },
      college: { heats: 3, duration: '02:00' }
    }
  },
  { 
    id: '400m', name: '400m Dash', type: 'sprint',
    levels: {
      middle: { heats: 3, duration: '02:30' },
      high: { heats: 6, duration: '03:00' },
      college: { heats: 3, duration: '03:30' }
    }
  },

  // Sprint Prelims/Finals variants — same duration as base, Finals default
  // to 1 heat (championship heat). Prelims inherit the base heats. Listed
  // here so they appear in Settings → Default Event Settings (customizable
  // per level) and in the "Add Event" picker in Configure Events.
  {
    id: '55m_prelims', name: '55m Dash Prelims', type: 'sprint',
    levels: {
      middle: { heats: 6, duration: '01:30' },
      high: { heats: 12, duration: '02:00' },
      college: { heats: 4, duration: '02:30' }
    }
  },
  {
    id: '55m_final', name: '55m Dash Final', type: 'sprint',
    levels: {
      middle: { heats: 1, duration: '01:30' },
      high: { heats: 1, duration: '02:00' },
      college: { heats: 1, duration: '02:30' }
    }
  },
  {
    id: '60m_prelims', name: '60m Dash Prelims', type: 'sprint',
    levels: {
      middle: { heats: 6, duration: '01:30' },
      high: { heats: 15, duration: '02:00' },
      college: { heats: 4, duration: '02:30' }
    }
  },
  {
    id: '60m_final', name: '60m Dash Final', type: 'sprint',
    levels: {
      middle: { heats: 1, duration: '01:30' },
      high: { heats: 1, duration: '02:00' },
      college: { heats: 1, duration: '02:30' }
    }
  },
  {
    id: '100m_prelims', name: '100m Dash Prelims', type: 'sprint',
    levels: {
      middle: { heats: 5, duration: '02:00' },
      high: { heats: 10, duration: '02:00' },
      college: { heats: 3, duration: '02:00' }
    }
  },
  {
    id: '100m_final', name: '100m Dash Final', type: 'sprint',
    levels: {
      middle: { heats: 1, duration: '02:00' },
      high: { heats: 1, duration: '02:00' },
      college: { heats: 1, duration: '02:00' }
    }
  },
  {
    id: '200m_prelims', name: '200m Dash Prelims', type: 'sprint',
    levels: {
      middle: { heats: 5, duration: '02:00' },
      high: { heats: 10, duration: '02:00' },
      college: { heats: 3, duration: '02:00' }
    }
  },
  {
    id: '200m_final', name: '200m Dash Final', type: 'sprint',
    levels: {
      middle: { heats: 1, duration: '02:00' },
      high: { heats: 1, duration: '02:00' },
      college: { heats: 1, duration: '02:00' }
    }
  },
  {
    id: '400m_prelims', name: '400m Dash Prelims', type: 'sprint',
    levels: {
      middle: { heats: 3, duration: '02:30' },
      high: { heats: 6, duration: '03:00' },
      college: { heats: 3, duration: '03:30' }
    }
  },
  {
    id: '400m_final', name: '400m Dash Final', type: 'sprint',
    levels: {
      middle: { heats: 1, duration: '02:30' },
      high: { heats: 1, duration: '03:00' },
      college: { heats: 1, duration: '03:30' }
    }
  },
  
  // Hurdles
  { 
    id: '55m_hurdles', name: '55m Hurdles', type: 'hurdles',
    levels: {
      middle: { heats: 4, duration: '02:00' },
      high: { heats: 7, duration: '03:00' },
      college: { heats: 3, duration: '03:30' }
    }
  },
  { 
    id: '60m_hurdles', name: '60m Hurdles', type: 'hurdles',
    levels: {
      middle: { heats: 4, duration: '02:00' },
      high: { heats: 7, duration: '03:00' },
      college: { heats: 3, duration: '03:30' }
    }
  },
  { 
    id: '100m_hurdles', name: '100m Hurdles', type: 'hurdles',
    levels: {
      middle: { heats: 3, duration: '03:00' },
      high: { heats: 6, duration: '03:30' },
      college: { heats: 3, duration: '04:00' }
    }
  },
  { 
    id: '110m_hurdles', name: '110m Hurdles', type: 'hurdles',
    levels: {
      middle: { heats: 3, duration: '03:00' },
      high: { heats: 6, duration: '03:30' },
      college: { heats: 3, duration: '04:00' }
    }
  },
  { 
    id: '200m_hurdles', name: '200m Hurdles', type: 'hurdles',
    levels: {
      middle: { heats: 3, duration: '03:00' },
      high: { heats: 4, duration: '03:30' },
      college: null
    }
  },
  { 
    id: '300m_hurdles', name: '300m Hurdles', type: 'hurdles',
    levels: {
      middle: null,
      high: { heats: 4, duration: '04:00' },
      college: null
    }
  },
  { 
    id: '400m_hurdles', name: '400m Hurdles', type: 'hurdles',
    levels: {
      middle: null,
      high: { heats: 3, duration: '04:00' },
      college: { heats: 3, duration: '04:30' }
    }
  },

  // Hurdles Prelims/Finals variants — mirror the base event's heat counts
  // for Prelims, and default Finals to 1 heat (championship heat). Same
  // duration as the base event. `null` levels stay `null` so the event
  // simply doesn't appear in those level pickers.
  {
    id: '55m_hurdles_prelims', name: '55m Hurdles Prelims', type: 'hurdles',
    levels: {
      middle: { heats: 4, duration: '02:00' },
      high: { heats: 7, duration: '03:00' },
      college: { heats: 3, duration: '03:30' }
    }
  },
  {
    id: '55m_hurdles_final', name: '55m Hurdles Final', type: 'hurdles',
    levels: {
      middle: { heats: 1, duration: '02:00' },
      high: { heats: 1, duration: '03:00' },
      college: { heats: 1, duration: '03:30' }
    }
  },
  {
    id: '60m_hurdles_prelims', name: '60m Hurdles Prelims', type: 'hurdles',
    levels: {
      middle: { heats: 4, duration: '02:00' },
      high: { heats: 7, duration: '03:00' },
      college: { heats: 3, duration: '03:30' }
    }
  },
  {
    id: '60m_hurdles_final', name: '60m Hurdles Final', type: 'hurdles',
    levels: {
      middle: { heats: 1, duration: '02:00' },
      high: { heats: 1, duration: '03:00' },
      college: { heats: 1, duration: '03:30' }
    }
  },
  {
    id: '100m_hurdles_prelims', name: '100m Hurdles Prelims', type: 'hurdles',
    levels: {
      middle: { heats: 3, duration: '03:00' },
      high: { heats: 6, duration: '03:30' },
      college: { heats: 3, duration: '04:00' }
    }
  },
  {
    id: '100m_hurdles_final', name: '100m Hurdles Final', type: 'hurdles',
    levels: {
      middle: { heats: 1, duration: '03:00' },
      high: { heats: 1, duration: '03:30' },
      college: { heats: 1, duration: '04:00' }
    }
  },
  {
    id: '110m_hurdles_prelims', name: '110m Hurdles Prelims', type: 'hurdles',
    levels: {
      middle: { heats: 3, duration: '03:00' },
      high: { heats: 6, duration: '03:30' },
      college: { heats: 3, duration: '04:00' }
    }
  },
  {
    id: '110m_hurdles_final', name: '110m Hurdles Final', type: 'hurdles',
    levels: {
      middle: { heats: 1, duration: '03:00' },
      high: { heats: 1, duration: '03:30' },
      college: { heats: 1, duration: '04:00' }
    }
  },
  {
    id: '200m_hurdles_prelims', name: '200m Hurdles Prelims', type: 'hurdles',
    levels: {
      middle: { heats: 3, duration: '03:00' },
      high: { heats: 4, duration: '03:30' },
      college: null
    }
  },
  {
    id: '200m_hurdles_final', name: '200m Hurdles Final', type: 'hurdles',
    levels: {
      middle: { heats: 1, duration: '03:00' },
      high: { heats: 1, duration: '03:30' },
      college: null
    }
  },
  {
    id: '300m_hurdles_prelims', name: '300m Hurdles Prelims', type: 'hurdles',
    levels: {
      middle: null,
      high: { heats: 4, duration: '04:00' },
      college: null
    }
  },
  {
    id: '300m_hurdles_final', name: '300m Hurdles Final', type: 'hurdles',
    levels: {
      middle: null,
      high: { heats: 1, duration: '04:00' },
      college: null
    }
  },
  {
    id: '400m_hurdles_prelims', name: '400m Hurdles Prelims', type: 'hurdles',
    levels: {
      middle: null,
      high: { heats: 3, duration: '04:00' },
      college: { heats: 3, duration: '04:30' }
    }
  },
  {
    id: '400m_hurdles_final', name: '400m Hurdles Final', type: 'hurdles',
    levels: {
      middle: null,
      high: { heats: 1, duration: '04:00' },
      college: { heats: 1, duration: '04:30' }
    }
  },
  
  // Middle Distance
  { 
    id: '800m', name: '800m Run', type: 'distance',
    levels: {
      middle: { heats: 2, duration: '04:00' },
      high: { heats: 3, duration: '04:00' },
      college: { heats: 2, duration: '05:00' }
    }
  },
  { 
    id: '1500m', name: '1500m Run', type: 'distance',
    levels: {
      middle: { heats: 1, duration: '07:30' },
      high: { heats: 1, duration: '08:00' },
      college: { heats: 1, duration: '09:30' }
    }
  },
  { 
    id: '1500m_racewalk', name: '1500m Race Walk', type: 'distance',
    levels: {
      middle: { heats: 1, duration: '12:00' },
      high: { heats: 1, duration: '12:00' },
      college: { heats: 1, duration: '12:00' }
    }
  },
  { 
    id: '1600m', name: '1600m Run', type: 'distance',
    levels: {
      middle: { heats: 1, duration: '06:30' },
      high: { heats: 1, duration: '07:00' },
      college: { heats: 1, duration: '07:30' }
    }
  },
  
  // Long Distance
  { 
    id: '3000m', name: '3000m Run', type: 'distance',
    levels: {
      middle: { heats: 1, duration: '14:30' },
      high: { heats: 1, duration: '14:30' },
      college: { heats: 1, duration: '17:00' }
    }
  },
  { 
    id: '3000m_racewalk', name: '3000m Race Walk', type: 'distance',
    levels: {
      middle: { heats: 1, duration: '24:30' },
      high: { heats: 1, duration: '24:30' },
      college: null
    }
  },
  { 
    id: '3000m_steeple', name: '3000m Steeplechase', type: 'distance',
    levels: {
      middle: { heats: 1, duration: '14:00' },
      high: { heats: 1, duration: '14:00' },
      college: { heats: 1, duration: '14:00' }
    }
  },
  { 
    id: '3200m', name: '3200m Run', type: 'distance',
    levels: {
      middle: { heats: 1, duration: '18:00' },
      high: { heats: 1, duration: '16:30' },
      college: { heats: 1, duration: '17:30' }
    }
  },
  { 
    id: '5000m', name: '5000m Run', type: 'distance',
    levels: {
      middle: { heats: 1, duration: '25:00' },
      high: { heats: 1, duration: '25:00' },
      college: { heats: 1, duration: '25:00' }
    }
  },
  { 
    id: '5000m_racewalk', name: '5000m Race Walk', type: 'distance',
    levels: {
      middle: { heats: 1, duration: '30:00' },
      high: { heats: 1, duration: '30:00' },
      college: { heats: 1, duration: '30:00' }
    }
  },
  
  // Relays (ordered by total distance)
  { 
    id: '4x100m', name: '4x100m Relay', type: 'relay',
    levels: {
      middle: { heats: 3, duration: '03:00' },
      high: { heats: 4, duration: '03:30' },
      college: { heats: 3, duration: '04:00' }
    }
  },
  { 
    id: '4x200m', name: '4x200m Relay', type: 'relay',
    levels: {
      middle: { heats: 3, duration: '03:00' },
      high: { heats: 4, duration: '03:30' },
      college: { heats: 3, duration: '04:00' }
    }
  },
  { 
    id: '4x400m', name: '4x400m Relay', type: 'relay',
    levels: {
      middle: { heats: 2, duration: '05:00' },
      high: { heats: 4, duration: '05:30' },
      college: { heats: 3, duration: '06:00' }
    }
  },
  { 
    id: '4x800m', name: '4x800m Relay', type: 'relay',
    levels: {
      middle: { heats: 2, duration: '10:30' },
      high: { heats: 2, duration: '11:00' },
      college: { heats: 1, duration: '11:30' }
    }
  },
  { 
    id: '4x1600m', name: '4x1600m Relay', type: 'relay',
    levels: {
      middle: null,
      high: { heats: 1, duration: '24:30' },
      college: { heats: 1, duration: '24:30' }
    }
  },
  { 
    id: 'shuttle_hurdle', name: 'Shuttle Hurdle Relay', type: 'relay',
    levels: {
      middle: { heats: 3, duration: '05:00' },
      high: { heats: 3, duration: '05:30' },
      college: null
    }
  },
  { 
    id: 'sprint_medley', name: 'Sprint Medley Relay', type: 'relay',
    levels: {
      middle: { heats: 3, duration: '05:30' },
      high: { heats: 3, duration: '06:00' },
      college: null
    }
  },
  { 
    id: 'distance_medley', name: 'Distance Medley Relay', type: 'relay',
    levels: {
      middle: { heats: 1, duration: '14:30' },
      high: { heats: 1, duration: '16:00' },
      college: { heats: 1, duration: '18:00' }
    }
  },
];

/**
 * Get the default heats and duration for an event based on level
 * @param {string} eventId - The event ID
 * @param {string} level - 'middle', 'high', 'college', or 'professional'
 * @returns {{ heats: number, duration: string } | null}
 */
export function getEventDefaultsForLevel(eventId, level) {
  const event = ALL_EVENTS.find(e => e.id === eventId);
  if (!event || !event.levels) return null;
  
  // Map 'professional' to 'college' as they use same defaults
  const mappedLevel = level === 'professional' ? 'college' : level;
  // Map 'middle-school' to 'middle' and 'high-school' to 'high'
  const normalizedLevel = mappedLevel.replace('-school', '').replace('-', '');
  
  return event.levels[normalizedLevel] || null;
}

/**
 * Check if an event is available for a given level
 * @param {string} eventId - The event ID
 * @param {string} level - 'middle', 'high', 'college', or 'professional'
 * @returns {boolean}
 */
export function isEventAvailableForLevel(eventId, level) {
  const defaults = getEventDefaultsForLevel(eventId, level);
  return defaults !== null;
}

// Generate default event settings from ALL_EVENTS (using high school as default)
function generateDefaultEventSettings() {
  const defaults = {};
  ALL_EVENTS.forEach(event => {
    // Use high school defaults as the base default
    const levelDefaults = event.levels?.high || event.levels?.middle || event.levels?.college;
    if (levelDefaults) {
      defaults[`${event.id}_heats`] = levelDefaults.heats;
      defaults[`${event.id}_duration`] = levelDefaults.duration;
    }
  });
  return defaults;
}

// Default settings
export const DEFAULT_SETTINGS = {
  // Appearance
  theme: 'light',
  
  // Meet Configuration - Time Between Events
  timeBetweenEventsIndoor: '0',
  timeBetweenEventsOutdoor: '0',
  
  // Regional preferences
  shortSprintDistance: '60m',
  shortHurdlesDistance: '60m',
  middleDistanceRun: '1600m',
  longHurdlesDistance: '400m',
  
  // Optional events
  shuttleHurdleRelay: false,
  raceWalk1500m: false,
  raceWalk3000m: false,
  raceWalk5000m: false,
  sprintMedleyRelay: false,
  distanceMedleyRelay: false,
  
  // Notifications
  notificationsEnabled: true,
  eventStartNotification: true,
  warningTime: '10',
  
  // Individual event defaults (generated)
  ...generateDefaultEventSettings(),

  // Multi-sport Settings-page baselines (Feb 2026). Includes
  // `defaultSportProfile`, per-sport transition keys, swim senior /
  // youth pacing rows, and XC race rows. See `sportConfig.js`.
  ...buildSettingsBaselineDefaults(),
};

/**
 * Get all settings, merged with defaults
 */
export function getSettings() {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (e) {
    console.error('Error reading settings:', e);
  }
  return DEFAULT_SETTINGS;
}

/**
 * Save settings to localStorage
 */
export function saveSettings(settings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    return true;
  } catch (e) {
    console.error('Error saving settings:', e);
    return false;
  }
}

/**
 * Get a single setting value
 */
export function getSetting(key) {
  const settings = getSettings();
  return settings[key] ?? DEFAULT_SETTINGS[key];
}

/**
 * Update a single setting
 */
export function updateSetting(key, value) {
  const settings = getSettings();
  settings[key] = value;
  return saveSettings(settings);
}

/**
 * Get default heats for a specific event by matching name
 */
export function getDefaultHeatsForEvent(eventName) {
  const settings = getSettings();
  const normalizedName = eventName.toLowerCase();
  
  // Try to match the event name to our known events
  for (const event of ALL_EVENTS) {
    const eventNameLower = event.name.toLowerCase();
    const eventId = event.id;
    
    // Check if the event name contains key identifiers
    if (normalizedName.includes(eventId.replace(/_/g, ' ')) ||
        normalizedName.includes(eventId.replace(/_/g, '')) ||
        eventNameLower.includes(normalizedName) ||
        normalizedName.includes(eventNameLower.split(' ')[0])) {
      return settings[`${eventId}_heats`] || event.defaultHeats;
    }
  }
  
  // Fallback matching by distance
  const distanceMatch = normalizedName.match(/(\d+)m/);
  if (distanceMatch) {
    const distance = distanceMatch[1];
    const matchedEvent = ALL_EVENTS.find(e => e.id.startsWith(distance + 'm') || e.id === distance + 'm');
    if (matchedEvent) {
      return settings[`${matchedEvent.id}_heats`] || matchedEvent.defaultHeats;
    }
  }
  
  return 3; // Default fallback
}

/**
 * Get default duration for a specific event by matching name
 */
export function getDefaultDurationForEvent(eventName) {
  const settings = getSettings();
  const normalizedName = eventName.toLowerCase();
  
  // Try to match the event name to our known events
  for (const event of ALL_EVENTS) {
    const eventNameLower = event.name.toLowerCase();
    const eventId = event.id;
    
    if (normalizedName.includes(eventId.replace(/_/g, ' ')) ||
        normalizedName.includes(eventId.replace(/_/g, '')) ||
        eventNameLower.includes(normalizedName) ||
        normalizedName.includes(eventNameLower.split(' ')[0])) {
      return settings[`${eventId}_duration`] || event.defaultDuration;
    }
  }
  
  // Fallback matching by distance
  const distanceMatch = normalizedName.match(/(\d+)m/);
  if (distanceMatch) {
    const distance = distanceMatch[1];
    const matchedEvent = ALL_EVENTS.find(e => e.id.startsWith(distance + 'm') || e.id === distance + 'm');
    if (matchedEvent) {
      return settings[`${matchedEvent.id}_duration`] || matchedEvent.defaultDuration;
    }
  }
  
  return '03:00'; // Default fallback
}

/**
 * Get event settings (heats and duration) for a specific event
 */
export function getEventDefaults(eventId) {
  const settings = getSettings();
  const event = ALL_EVENTS.find(e => e.id === eventId);
  
  if (event) {
    return {
      heats: settings[`${eventId}_heats`] || event.defaultHeats,
      duration: settings[`${eventId}_duration`] || event.defaultDuration
    };
  }
  
  return { heats: 3, duration: '03:00' };
}

/**
 * Get time between events based on meet type (indoor/outdoor)
 * @param {string} meetType - 'indoor' or 'outdoor'
 * @returns {string} Time in minutes (e.g., '0', '0.5', '1')
 */
export function getTimeBetweenEvents(meetType) {
  const settings = getSettings();
  if (meetType === 'indoor') {
    return settings.timeBetweenEventsIndoor || '0';
  }
  return settings.timeBetweenEventsOutdoor || '0';
}

export default {
  getSettings,
  saveSettings,
  getSetting,
  updateSetting,
  getDefaultHeatsForEvent,
  getDefaultDurationForEvent,
  getEventDefaults,
  getTimeBetweenEvents,
  ALL_EVENTS,
  DEFAULT_SETTINGS
};
